import { useQuery } from "@tanstack/react-query";

import { api, LegacyWarningSetting, Location } from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import { useMaritimeWarningPageDefaults } from "@/features/shared/hooks/use-warning-page-defaults";
import { readMaritimeWarningPageSettings } from "@/lib/maritime-warning-page-settings.utils";

export const keys = {
  dashboardLocations: (dashboardId: string) =>
    ["dashboard", dashboardId, "locations"] as const,
  dashboardWarnings: (dashboardId: string) =>
    ["dashboard", dashboardId, "warnings"] as const,
  dashboardMaritimeWarningSettings: (dashboardId: string) =>
    ["dashboard", dashboardId, "maritime_warning_settings"] as const,
  dashboardPublicWarningSettings: (dashboardId: string) =>
    ["dashboard", dashboardId, "public_warning_settings"] as const,
};

export const useDashboardLocationsQuery = (dashboardId: string) => {
  return useQuery({
    queryKey: keys.dashboardLocations(dashboardId),
    queryFn: (): Promise<Location[]> =>
      unwrapAxiosData(api.locations.getLocationsForDashboard(dashboardId)),
  });
};

export const useDashboardWarningsQuery = (dashboardId: string) => {
  return useQuery({
    queryKey: keys.dashboardWarnings(dashboardId),
    queryFn: (): Promise<LegacyWarningSetting> =>
      unwrapAxiosData(
        api.legacyWarningSettings.getLegacyWarningSettingsForDashboard(dashboardId),
      ),
  });
};

export const useDashboardPublicWarningSettingsQuery = (dashboardId: string) => {
  return useQuery({
    queryKey: keys.dashboardPublicWarningSettings(dashboardId),
    queryFn: () =>
      unwrapAxiosData(
        api.publicWarningSettings.getPublicWarningSettingsForDashboard(dashboardId),
      ),
    enabled: Boolean(dashboardId),
  });
};

/** Maritime switch, domains and page copy in app terms (copy resolved over locale defaults). */
export const useDashboardMaritimeWarningSettingsQuery = (dashboardId: string) => {
  const defaults = useMaritimeWarningPageDefaults();
  return useQuery({
    queryKey: keys.dashboardMaritimeWarningSettings(dashboardId),
    queryFn: () =>
      unwrapAxiosData(
        api.maritimeWarningSettings.getMaritimeWarningSettingsForDashboard(dashboardId),
      ),
    select: (settings) => readMaritimeWarningPageSettings(settings, defaults),
    enabled: Boolean(dashboardId),
  });
};
